"use client";

import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { IoSparkles } from "react-icons/io5";
import { MdArrowOutward } from "react-icons/md";
import AnimatedIcon from "../ui/animated-icon";
import siteData from "../../data/site-data";
import {
  staggerContainer,
  staggerItem,
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  textReveal,
} from "../../lib/animations";
import PortfolioModal from "../modal/portfolio-modal";

export default function Footer() {
  const { footer } = siteData;
  const [open, setOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("");
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const socialIcons = {
    instagram: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
    facebook: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    twitter: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    linkedin: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  };

  const handleModal = (type: string) => {
    setOpen(!open);
    setModalType(type);
  };

  return (
    <motion.footer
      ref={ref}
      className="relative overflow-hidden bg-[#050816] py-16"
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
    >
      <PortfolioModal
        open={open}
        onClose={() => setOpen(false)}
        modalType={modalType}
      />
  
      {/* Background Glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute left-0 top-0 h-[500px] w-[500px] rounded-full bg-[#5B5BFF]/10 blur-[180px]" />
        <div className="absolute right-0 bottom-0 h-[500px] w-[500px] rounded-full bg-[#ED7C3F]/10 blur-[180px]" />
      </div>
  
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Footer Card */}
        <div
          className="rounded-[28px] border border-white/10 p-8 md:p-10"
          style={{
            background:
              "linear-gradient(180deg, rgba(17,24,39,0.92), rgba(15,23,42,0.92))",
            backdropFilter: "blur(12px)",
          }}
        >
          {/* Logo Row */}
          <motion.div
            className="flex items-center mb-10"
            variants={fadeInUp}
          >
            <motion.div
              className="flex items-center"
              whileHover={{ scale: 1.05 }}
            >
              <span className="text-2xl font-bold text-white">
                {footer.logo.text}
              </span>
  
              <AnimatedIcon
                animation="pulse"
                className="w-5 h-5 text-[#ED7C3F] ml-2"
              >
                <IoSparkles />
              </AnimatedIcon>
            </motion.div>
  
            <motion.div
              className="flex-1 h-px bg-white/10 ml-8"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{
                duration: 0.8,
                delay: 0.3,
              }}
            />
          </motion.div>
  
          {/* Links */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-12"
            variants={staggerContainer}
          >
            {footer.links.map((column, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
              >
                <motion.h3
                  className="text-white text-lg font-semibold mb-5"
                  whileHover={{ y: -2 }}
                >
                  {column.title}
                </motion.h3>
  
                <motion.ul
                  className="space-y-3"
                  variants={staggerContainer}
                >
                  {column.links.map((link, linkIndex) => (
                    <motion.li
                      key={linkIndex}
                      variants={staggerItem}
                    >
                      <motion.a
                        href={link.href}
                        className="group flex items-center gap-2 text-white/50 hover:text-white transition-colors"
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => {
                          if (link.name === "Portfolio")
                            handleModal("portfolio");
  
                          if (link.name === "Case Studies")
                            handleModal("caseStudy");
                        }}
                      >
                        {"icon" in link &&
                          link.icon &&
                          socialIcons[
                            link.icon as keyof typeof socialIcons
                          ] && (
                            <motion.div
                              whileHover={{
                                scale: 1.15,
                                rotate: 5,
                              }}
                            >
                              {
                                socialIcons[
                                  link.icon as keyof typeof socialIcons
                                ]
                              }
                            </motion.div>
                          )}
  
                        <span>{link.name}</span>
  
                        {["Portfolio", "Case Studies"].includes(
                          link.name
                        ) && (
                          <MdArrowOutward className="invisible group-hover:visible" />
                        )}
                      </motion.a>
                    </motion.li>
                  ))}
                </motion.ul>
              </motion.div>
            ))}
          </motion.div>
  
          {/* Bottom Bar */}
          <motion.div
            className="border-t border-white/10 pt-8"
            variants={fadeInUp}
          >
            <motion.div
              className="flex flex-col md:flex-row justify-between items-center gap-4"
              variants={staggerContainer}
            >
              <motion.div
                className="text-white/35 text-sm"
                variants={staggerItem}
              >
                {footer.copyright}
              </motion.div>
  
              <motion.div
                className="flex items-center gap-6 text-sm"
                variants={staggerContainer}
              >
                {footer.legal.map((link, index) => (
                  <motion.a
                    key={index}
                    href={link.href}
                    className="text-white/35 hover:text-white transition-colors"
                    variants={staggerItem}
                    whileHover={{ y: -1 }}
                  >
                    {link.name}
                  </motion.a>
                ))}
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.footer>
  );
}
